import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  X,
  Upload,
  Trash2,
  Check,
  Video,
  FileText,
  Image as ImageIcon,
  ChevronDown,
} from "lucide-react";
import apiClient from "../../api/apiClient";
import { API_BASE_URL } from "../../api/header";
import { Spinner } from "@/components/ui/spinner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import PropTypes from "prop-types";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const MediaLibraryComponent = ({
  isOpen,
  onClose,
  onSelect,
  isMultiple: propIsMultiple = false,
  selectionMode = "multiple",
  selectedIds = [],
}) => {
  const isMultiple = propIsMultiple || selectionMode === "multiple";
  const permissions = useSelector((state) => state.userpermission_data) || [];

  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };

  const [mediaItems, setMediaItems] = useState([]);
  const [selectedMedia, setSelectedMedia] = useState(selectedIds);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedMedia(selectedIds);
      setMediaItems([]);
      setCurrentPage(1);
      loadMedia(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadMedia = async (page = 1) => {
    if (!hasPermission("media library view")) return;
    setLoading(true);
    try {
      const res = await apiClient.get(`${API_BASE_URL}/media`, {
        params: { page },
      });

      if (res.status === 200 || res.status === 201) {
        const rawData = res.data.data || [];
        const newData = [...rawData].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at),
        );

        if (page === 1) {
          setMediaItems(newData);
        } else {
          setMediaItems((prev) => [...prev, ...newData]);
        }

        const meta = res.data.meta || res.data;
        setCurrentPage(meta.current_page || 1);
        setHasMore((meta.current_page || 1) < (meta.last_page || 1));
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to fetch media");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    if (!hasPermission("media create")) {
      toast.error("You do not have permission to upload media");
      return;
    }
    const files = Array.from(e.target.files);

    if (files.length === 0) return;

    setUploading(true);

    try {
      for (const file of files) {
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name} exceeds 10MB limit`);
          continue;
        }

        const formData = new FormData();
        formData.append("file", file);

        await apiClient.post(`/media`, formData);
      }

      setMediaItems([]);
      setCurrentPage(1);
      loadMedia(1);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to upload media");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!hasPermission("media delete")) {
      toast.error("You do not have permission to delete media");
      return;
    }
    try {
      const res = await apiClient.delete(`${API_BASE_URL}/media/${deleteId}`);

      if (res.status === 204) {
        toast.success("Media deleted successfully");

        setSelectedMedia((prev) =>
          prev.filter((mediaId) => mediaId !== deleteId),
        );

        setMediaItems([]);
        loadMedia(1);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete media");
    } finally {
      setIsDeleteDialogOpen(false);
    }
  };

  const handleMediaClick = (mediaId) => {
    if (isMultiple) {
      setSelectedMedia((prev) => {
        if (prev.includes(mediaId)) {
          return prev.filter((id) => id !== mediaId);
        } else {
          return [...prev, mediaId];
        }
      });
    } else {
      setSelectedMedia([mediaId]);
    }
  };

  const handleInsert = () => {
    if (!hasPermission("media library assign")) {
      toast.error("You do not have permission to assign media");
      return;
    }
    if (selectedMedia.length === 0) {
      toast.error("Please select at least one media item");
      return;
    }

    if (onSelect) {
      if (isMultiple) {
        const selectedObjects = mediaItems.filter((item) =>
          selectedMedia.includes(item.id),
        );
        onSelect(selectedMedia, selectedObjects);
      } else {
        const selectedId = selectedMedia[0];
        const selectedObject = mediaItems.find(
          (item) => item.id === selectedId,
        );
        onSelect(selectedId, selectedObject);
      }
    }
    onClose();
  };

  const isImageFile = (mimeType) => {
    return mimeType && mimeType.startsWith("image/");
  };

  const isVideoFile = (mimeType) => {
    return mimeType && mimeType.startsWith("video/");
  };

  const getFileIcon = (mimeType) => {
    if (isImageFile(mimeType)) return <ImageIcon size={40} />;
    if (isVideoFile(mimeType)) return <Video size={40} />;
    return <FileText size={40} />;
  };

  if (!isOpen) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-6xl p-0 overflow-hidden h-[85vh] flex flex-col sm:rounded-xl border shadow-2xl">
          <DialogHeader className="px-8 py-6 shrink-0 border-b bg-card">
            <div className="flex items-center justify-between mr-8">
              <div>
                <DialogTitle className="text-2xl font-black tracking-tight text-slate-900">
                  Media Library
                </DialogTitle>
                <DialogDescription className="text-slate-500 mt-1 font-medium">
                  Manage your storage and select assets for your products
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="flex-none bg-muted/80 p-6 border-b flex flex-wrap justify-between items-center gap-4">
            <div className="flex items-center gap-4">
              {hasPermission("media create") && (
                <label className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-bold rounded-lg cursor-pointer hover:bg-blue-700 transition-all shadow-sm">
                  {uploading ? (
                    <Spinner size={18} className="shrink-0" />
                  ) : (
                    <Upload size={18} className="shrink-0" />
                  )}
                  <span>{uploading ? "Uploading..." : "Upload Files"}</span>
                  <input
                    type="file"
                    multiple
                    accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx"
                    onChange={handleFileUpload}
                    disabled={uploading}
                    className="hidden"
                  />
                </label>
              )}
              <div className="h-8 w-px bg-slate-200 hidden md:block" />
              <p className="text-xs text-slate-500 hidden md:block font-medium italic">
                Maximum upload size: 10MB per file
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="bg-card px-4 py-2 rounded-lg border border-border text-sm font-bold text-muted-foreground shadow-sm">
                {selectedMedia?.length || 0} Selected
              </div>
              <div className="bg-card px-4 py-2 rounded-lg border border-border text-sm font-bold text-muted-foreground shadow-sm">
                {mediaItems.length} Total
              </div>
            </div>
          </div>

          <div className="flex-grow overflow-y-auto p-8 scrollbar-thin bg-card">
            {loading && currentPage === 1 ? (
              <div className="flex flex-col items-center justify-center py-24">
                <Spinner size={48} className="mb-6 text-blue-600" />
                <p className="text-muted-foreground animate-pulse font-bold tracking-widest text-xs uppercase">
                  Accessing Cloud Storage...
                </p>
              </div>
            ) : mediaItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24 text-muted-foreground">
                <div className="bg-muted p-8 rounded-full mb-6 border border-border">
                  <Upload size={48} className="opacity-20" />
                </div>
                <h5 className="font-black text-xl text-slate-900 mb-2">
                  Your library is empty
                </h5>
                <p className="max-w-[280px] text-center leading-relaxed font-medium">
                  Drag and drop files here or use the upload button above to
                  start building your library.
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6">
                  {mediaItems.map((media) => (
                    <div
                      key={media.id}
                      className={cn(
                        "group relative border-2 rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-xl bg-card",
                        selectedMedia.includes(media.id)
                          ? "border-blue-600 ring-4 ring-blue-50 scale-[0.98]"
                          : "border-border hover:border-blue-200",
                      )}
                      onClick={() => handleMediaClick(media.id)}
                    >
                      {/* Selection Indicator */}
                      {selectedMedia.includes(media.id) && (
                        <div className="absolute top-2.5 left-2.5 z-10 bg-blue-600 text-white rounded-full p-1.5 shadow-lg animate-in zoom-in-50 duration-300">
                          <Check size={14} strokeWidth={4} />
                        </div>
                      )}

                      {/* Delete Button */}
                      {hasPermission("media delete") && (
                        <button
                          className="absolute top-2.5 right-2.5 z-10 bg-card/95 backdrop-blur-sm text-rose-500 rounded-lg p-2 shadow-sm border border-border opacity-0 group-hover:opacity-100 transition-all hover:bg-rose-500 hover:text-white"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(media.id);
                          }}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}

                      {/* Media Thumbnail */}
                      <div className="aspect-square bg-muted flex items-center justify-center overflow-hidden">
                        {isImageFile(media.mime_type) ? (
                          <img
                            src={media.url}
                            alt={media.name}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        ) : (
                          <div className="flex flex-col items-center gap-3 text-slate-300 group-hover:text-blue-600 transition-colors">
                            {getFileIcon(media.mime_type)}
                          </div>
                        )}
                      </div>

                      {/* Media Info */}
                      <div className="p-3 bg-card text-[11px] border-t border-border">
                        <p
                          className="font-bold truncate text-slate-900 mb-0.5"
                          title={media.name || media.file_name}
                        >
                          {media.name || media.file_name}
                        </p>
                        <div className="flex justify-between items-center text-muted-foreground font-bold uppercase tracking-tighter">
                          <span>
                            {media.size &&
                              `${(media.size / 1024).toFixed(0)} KB`}
                          </span>
                          <span className="opacity-60 bg-slate-100 px-1.5 py-0.5 rounded">
                            {media.mime_type?.split("/")[1]?.substring(0, 4)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Load More Button */}
                {hasMore && (
                  <div className="flex justify-center mt-12 pb-12">
                    <Button
                      variant="outline"
                      onClick={() => {
                        const nextPage = currentPage + 1;
                        setCurrentPage(nextPage);
                        loadMedia(nextPage);
                      }}
                      disabled={loading}
                      className="rounded-lg px-10 h-11 border-border hover:border-blue-600 hover:text-blue-600 transition-all font-bold"
                    >
                      {loading ? (
                        <Spinner size={18} className="mr-2" />
                      ) : (
                        <ChevronDown size={18} className="mr-2" />
                      )}
                      Load More Files
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>

          <DialogFooter className="bg-muted px-8 py-5 border-t shrink-0">
            <div className="flex items-center justify-between w-full">
              <div className="text-sm font-bold text-slate-500">
                {selectedMedia.length}{" "}
                {selectedMedia.length === 1 ? "File" : "Files"} Selected
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={onClose}
                  className="rounded-lg px-6 h-11 font-bold border-border"
                >
                  Cancel
                </Button>
                {hasPermission("media library assign") && (
                  <Button
                    onClick={handleInsert}
                    disabled={selectedMedia.length === 0}
                    className="rounded-lg px-8 h-11 font-bold shadow-md bg-blue-600 hover:bg-blue-700"
                  >
                    Insert Selected
                  </Button>
                )}
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              media file and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

MediaLibraryComponent.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  isMultiple: PropTypes.bool,
  selectedIds: PropTypes.array,
};

export default MediaLibraryComponent;
